import React, {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useState,
  } from "react";

import { useHistory, useLocation } from "react-router-dom";
import { useLocalStorage } from "./useLocalStorage";
import supabase from "utils/supabaseClient";
import { loginUser } from "data/api/auth/auth";
import { registerUser } from "data/api/auth/signup";
import toast from "react-hot-toast";
import history from "hooks/history";
import { DateRage } from "components/HeroSearchForm2/StaySearchForm";
import { GuestsObject } from "components/HeroSearchForm2Mobile/GuestsInput";
interface User{
  "id": number,
  "name": string,
  "email": string,
  "phone_number": string,
  "gender": string,
  "account_type": number,
  "email_verified_at": string | null,
  "created_at": string,
  "updated_at": string
}
export interface SearchContextType {
  // We defined the user type in `index.d.ts`, but it's
  // a simple object with email, name and password.
  guest?:  GuestsObject;
  dates?: DateRage;
  location?: string;
}
  interface AuthContextType {
    // We defined the user type in `index.d.ts`, but it's
    // a simple object with email, name and password.
    user?: User ;
    token?: string
    search?: SearchContextType,
    callSearch: (value: SearchContextType) => void
    login: (login: string, password: string) => void;
    signUp: (email: string, password: string, name: string, phone_number: string) => void;
    signInWithGoogle: () => void,
    signInWithFacebook: () => void,
    logout: () => void;
    booking?: any;
    setBooking: (value: any) => void;
  }
  
  const AuthContext = createContext<AuthContextType>(
    {} as AuthContextType
  );
  
  // Export the provider as we need to wrap the entire app with it
  export function AuthProvider({
    children,
  }: {
    children: ReactNode;
  }): JSX.Element {
    const [user, setUser] = useLocalStorage("user", null);
    const [booking, setBooking] = useLocalStorage("booking", null);
    const [token, setToken] = useLocalStorage("token", null);
    const [search, setSearch] = useLocalStorage("search", null);
  
    const login =  async (login: string, password: string) => {

          const result = await loginUser({login, password})
          console.log(result)
          if(result && result.success === true){
            setUser(result.user)
            setToken(result.token)
            toast.success("Login successful")
            history.push('/')
          }else{
            toast.error("Email or Password is incorrect")
          }
          
    }

    const callSearch = (value: SearchContextType) => {
        setSearch(value)
    }
  
    // Sends sign up details to the server. On success we just apply
    // the created user to the state.
    const signUp = async (email: string,password: string, name: string,phone_number: string) => {
        const data = await registerUser({email,password,name,phone_number})
        if(data && data.success === true){
          setUser(data.user)
          setToken(data.token)
          toast.success("Signup successful")
          history.push('/')
        }else{
          toast.error("Incorrect credentials")
        }
        
    }

    async function signInWithGoogle() {
      const { error } = await supabase.auth.signIn({
        provider: 'google',
      })
      if(!error){
        let user = supabase.auth.user()
        setUser(user)
      }
    }
    async function signInWithFacebook() {
      const { error } = await supabase.auth.signIn({
        provider: 'facebook',
      })
      if(!error){
        let user = supabase.auth.user()
        setUser(user)
      }
    }
  
    // Call the logout endpoint and then remove the user
    // from the state.
    const logout = async () => {   
            setUser(null)
    }
  
    // Make the provider update only when it should.
    // We only want to force re-renders if the user,
    // loading or error states change.
    //
    // Whenever the `value` passed into a provider changes,
    // the whole tree under the provider re-renders, and
    // that can be very costly! Even in this case, where
    // you only get re-renders when logging in and out
    // we want to keep things very performant.
    const memoedValue = useMemo(
      () => ({
        user,
        booking,
        setBooking,
        token,
        login,
        search,
        callSearch,
        signUp,
        signInWithGoogle,
        signInWithFacebook,
        logout,
      }),
      [user,token,search]
    );
  
    // We only want to render the underlying app after we
    // assert for the presence of a current user.
    return (
      <AuthContext.Provider value={memoedValue}>
        { children}
      </AuthContext.Provider>
    );
  }
  
  // Let's only export the `useAuth` hook instead of the context.
  // We only want to use the hook directly and never the context component.
  export default function useAuth() {
    return useContext(AuthContext);
  }